import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, UserCheck, Search, CheckCircle, XCircle, Camera, Package,
  AlertTriangle, Truck, RotateCcw, Printer, ScanBarcode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { compressImageFiles } from '@/utils/imageCompression';
import { PjoOutcomePick } from '@/components/processingJo/PjoOutcomePick';

const DAMAGE_CATEGORIES = ['Mill Damage', 'Weaving Damage', 'Raw Material Damage'];

const DamageChecking = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const usnInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Mode: 'staff' | 'scan' | 'return-to-supplier'
  const [mode, setMode] = useState('staff');

  // Staff
  const [staffValue, setStaffValue] = useState('');
  const [staff, setStaff] = useState(null);
  const [validating, setValidating] = useState(false);

  // USN scanning
  const [usnValue, setUsnValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [currentUsn, setCurrentUsn] = useState(null); // looked-up USN details
  const [pjoOutcome, setPjoOutcome] = useState(''); // REPAIRED | REJECTED for job-order pieces
  const [processedList, setProcessedList] = useState([]);

  // Damage dialog
  const [showDamageDialog, setShowDamageDialog] = useState(false);
  const [damageTypes, setDamageTypes] = useState([]);
  const [selectedDamages, setSelectedDamages] = useState([]);
  const [damageSearch, setDamageSearch] = useState('');
  const [damageImages, setDamageImages] = useState({}); // { damageCode: [url, ...] }
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeDamageCode, setActiveDamageCode] = useState('');
  const [damageCategory, setDamageCategory] = useState('');

  // No Exchange selection
  const [noExchangeItems, setNoExchangeItems] = useState([]);
  const [noExchangeSearch, setNoExchangeSearch] = useState('');
  const [selectedNoExchange, setSelectedNoExchange] = useState(null);
  const [loadingNE, setLoadingNE] = useState(false);

  // Return to supplier
  const [rtsStaffValue, setRtsStaffValue] = useState('');
  const [rtsStaff, setRtsStaff] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [rtsUsnValue, setRtsUsnValue] = useState('');
  // Single-scan packing: completed boxes + the currently-open box being filled.
  // Each box is an array of USN detail objects.
  const [rtsBoxes, setRtsBoxes] = useState([]);
  const [rtsCurrentBox, setRtsCurrentBox] = useState([]);
  const [submittingRts, setSubmittingRts] = useState(false);
  const [rtsResult, setRtsResult] = useState(null);
  const [boxes, setBoxes] = useState([]); // boxes created by the submit

  const rtsStaffRef = useRef(null);
  const rtsUsnRef = useRef(null);

  const focusUsn = useCallback(() => {
    setTimeout(() => usnInputRef.current?.focus(), 80);
  }, []);

  // Fetch damage types on mount
  useEffect(() => {
    apiClient.get('/damage-checking/damage-types').then(r => setDamageTypes(r.data.damages || [])).catch(() => {});
  }, []);

  // === STAFF VALIDATION ===
  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    const id = staffValue.trim();
    if (!id || validating) return;
    setValidating(true);
    try {
      const resp = await apiClient.post('/damage-checking/validate-staff', { staff_id: id });
      setStaff({ staff_id: resp.data.staff_id, staff_name: resp.data.staff_name });
      setStaffValue('');
      toast.success(`Welcome, ${resp.data.staff_name}`);
      setMode('scan');
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid Staff ID');
      setStaffValue('');
    } finally {
      setValidating(false);
    }
  };

  // === USN LOOKUP ===
  const handleUsnLookup = async (e) => {
    e.preventDefault();
    const code = usnValue.trim();
    if (!code || scanning) return;
    setScanning(true);
    setCurrentUsn(null);
    setPjoOutcome('');
    try {
      const resp = await apiClient.get(`/damage-checking/lookup-usn?usn_code=${encodeURIComponent(code)}`);
      setCurrentUsn(resp.data);
      setUsnValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'USN not found');
      setUsnValue('');
      focusUsn();
    } finally {
      setScanning(false);
    }
  };

  // Job-order pieces must be marked Repaired / Rejected before any decision.
  const outcomeMissing = () => {
    if (currentUsn?.pjo_id && !pjoOutcome) {
      toast.error(`USN is on job order ${currentUsn.pjo_id} — mark it Repaired or Rejected first`);
      return true;
    }
    return false;
  };
  const outcomeBody = () => (currentUsn?.pjo_id ? { pjo_outcome: pjoOutcome } : {});
  const outcomeNote = (msg) => (currentUsn?.pjo_id
    ? `${msg} · ${pjoOutcome === 'REJECTED' ? 'counted as a loss on' : 'rejoins'} job order ${currentUsn.pjo_id}` : msg);

  // === CLEARED ===
  const handleCleared = async () => {
    if (!currentUsn || outcomeMissing()) return;
    try {
      const resp = await apiClient.post('/damage-checking/process-usn', {
        usn_code: currentUsn.usn_code,
        cleared: true,
        ...outcomeBody(),
      });
      toast.success(outcomeNote(resp.data.message));
      setProcessedList(prev => [{ ...currentUsn, result: 'CLEARED', pjo_outcome: currentUsn.pjo_id ? pjoOutcome : '' }, ...prev]);
      setCurrentUsn(null);
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process');
    }
  };

  // === NOT CLEARED — open damage dialog ===
  const handleNotCleared = () => {
    if (outcomeMissing()) return;
    setSelectedDamages([]);
    setDamageImages({});
    setDamageSearch('');
    setDamageCategory('');
    setSelectedNoExchange(null);
    setNoExchangeSearch('');
    setNoExchangeItems([]);
    setShowDamageDialog(true);
  };

  const toggleDamage = (code) => {
    setSelectedDamages(prev =>
      prev.includes(code) ? prev.filter(d => d !== code) : [...prev, code]
    );
  };

  const handleCameraCapture = (damageCode) => {
    setActiveDamageCode(damageCode);
    cameraInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const rawFiles = e.target.files;
    if (!rawFiles?.length || !activeDamageCode) return;
    setUploadingImage(true);
    try {
      // Compress before upload (parity with gate entries) — turns a 5-15 MB
      // camera photo into ~200-500 KB, far less likely to be blocked/limited
      // by a restrictive network proxy.
      const files = await compressImageFiles(Array.from(rawFiles));
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', `damage_checking/${currentUsn?.usn_code || 'unknown'}`);
        const resp = await apiClient.post('/storage/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setDamageImages(prev => ({
          ...prev,
          [activeDamageCode]: [...(prev[activeDamageCode] || []), resp.data.url]
        }));
        toast.success('Photo uploaded');
      }
    } catch (err) {
      // Surface the REAL failure so network issues (proxy block, timeout,
      // size cap) are visible instead of a generic message.
      let detail;
      if (err.code === 'ECONNABORTED') {
        detail = 'request timed out (slow / blocked network)';
      } else if (err.response) {
        detail = `server responded ${err.response.status}${err.response.data?.detail ? ' – ' + err.response.data.detail : ''}`;
      } else if (err.request) {
        detail = 'no response — network blocked the upload (firewall/proxy/Wi-Fi)';
      } else {
        detail = err.message || 'unknown error';
      }
      toast.error(`Failed to upload photo: ${detail}`, { duration: 10000 });
      console.error('Damage photo upload failed:', err);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  // Search no exchange items
  const searchNoExchange = async (q) => {
    setNoExchangeSearch(q);
    if (q.length < 2) { setNoExchangeItems([]); return; }
    setLoadingNE(true);
    try {
      const resp = await apiClient.get(`/damage-checking/no-exchange-items?search=${encodeURIComponent(q)}`);
      setNoExchangeItems(resp.data.items || []);
    } catch { setNoExchangeItems([]); }
    finally { setLoadingNE(false); }
  };

  // Submit damage (design transfer to No Exchange)
  const handleSubmitDamage = async () => {
    if (!damageCategory) { toast.error('Select a damage category'); return; }
    if (selectedDamages.length === 0) { toast.error('Select at least one damage type'); return; }
    for (const dc of selectedDamages) {
      if (!damageImages[dc]?.length) { toast.error('Upload a photo for each damage'); return; }
    }
    if (!selectedNoExchange) { toast.error('Select a No Exchange item for design transfer'); return; }

    const damagesData = selectedDamages.map(code => {
      const dt = damageTypes.find(d => d.damage_code === code);
      return { damage_code: code, damage_name: dt?.damage_name || '', image_urls: damageImages[code] || [] };
    });
    const allPhotos = Object.values(damageImages).flat();

    try {
      const resp = await apiClient.post('/damage-checking/process-usn', {
        usn_code: currentUsn.usn_code,
        cleared: false,
        damages: damagesData,
        damage_category: damageCategory,
        design_transfer_barcode: selectedNoExchange.barcode,
        design_transfer_design_name: selectedNoExchange.design_name,
        photo_urls: allPhotos,
        ...outcomeBody(),
      });
      toast.success(outcomeNote(resp.data.message));
      setProcessedList(prev => [{ ...currentUsn, result: 'DESIGN_TRANSFERRED', ne_barcode: selectedNoExchange.barcode, damage_category: damageCategory, pjo_outcome: currentUsn.pjo_id ? pjoOutcome : '' }, ...prev]);
      setCurrentUsn(null);
      setShowDamageDialog(false);
      focusUsn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process');
    }
  };

  // === RETURN TO SUPPLIER ===
  const openReturnToSupplier = async () => {
    setMode('return-to-supplier');
    setRtsStaff(null);
    setRtsStaffValue('');
    setSelectedVendor('');
    setRtsBoxes([]);
    setRtsCurrentBox([]);
    setRtsResult(null);
    setBoxes([]);
    try {
      const resp = await apiClient.get('/damage-checking/vendors');
      setVendors(resp.data.vendors || []);
    } catch { setVendors([]); }
    setTimeout(() => rtsStaffRef.current?.focus(), 100);
  };

  const handleRtsStaffSubmit = async (e) => {
    e.preventDefault();
    const id = rtsStaffValue.trim();
    if (!id) return;
    try {
      const resp = await apiClient.post('/damage-checking/validate-staff', { staff_id: id });
      setRtsStaff({ staff_id: resp.data.staff_id, staff_name: resp.data.staff_name });
      setRtsStaffValue('');
      setTimeout(() => rtsUsnRef.current?.focus(), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid Staff ID');
    }
  };

  // All USN codes scanned so far (across completed boxes + the open box).
  const allRtsUsnCodes = () => [...rtsBoxes.flat(), ...rtsCurrentBox].map(u => u.usn_code);

  const handleRtsUsnScan = async (e) => {
    e.preventDefault();
    const code = rtsUsnValue.trim();
    if (!code) return;
    if (allRtsUsnCodes().includes(code)) { toast.error('Already scanned'); setRtsUsnValue(''); return; }
    try {
      const resp = await apiClient.get(`/damage-checking/lookup-usn?usn_code=${encodeURIComponent(code)}&for_return=true`);
      setRtsCurrentBox(prev => [...prev, resp.data]);
      setRtsUsnValue('');
      setTimeout(() => rtsUsnRef.current?.focus(), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'USN not eligible');
      setRtsUsnValue('');
    }
  };

  // Close the open box and start a fresh one — next scans go into the new box.
  const closeCurrentBox = () => {
    if (rtsCurrentBox.length === 0) { toast.error('Scan at least one USN before closing the box'); return; }
    setRtsBoxes(prev => [...prev, rtsCurrentBox]);
    setRtsCurrentBox([]);
    toast.success(`Box ${rtsBoxes.length + 1} closed — start scanning the next box`);
    setTimeout(() => rtsUsnRef.current?.focus(), 50);
  };

  const removeCurrentUsn = (idx) => setRtsCurrentBox(prev => prev.filter((_, i) => i !== idx));

  const removeBoxUsn = (boxIdx, idx) => {
    setRtsBoxes(prev => {
      const next = prev.map((box, bi) => bi === boxIdx ? box.filter((_, i) => i !== idx) : box);
      return next.filter(box => box.length > 0); // drop a box if it became empty
    });
  };

  const handleRtsSubmit = async () => {
    // Combine the open box with the completed ones.
    const finalBoxes = rtsCurrentBox.length > 0 ? [...rtsBoxes, rtsCurrentBox] : [...rtsBoxes];
    const flatUsns = finalBoxes.flat();
    if (!rtsStaff || !selectedVendor || flatUsns.length === 0) {
      toast.error('Staff, vendor, and at least one USN required');
      return;
    }
    setSubmittingRts(true);
    try {
      const resp = await apiClient.post('/damage-checking/return-to-supplier', {
        staff_id: rtsStaff.staff_id,
        staff_name: rtsStaff.staff_name,
        vendor_name: selectedVendor,
        usn_codes: flatUsns.map(u => u.usn_code),
        boxes: finalBoxes.map(group => group.map(u => u.usn_code)),
      });
      toast.success(resp.data.message);
      setRtsResult(resp.data);
      setBoxes(resp.data.boxes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmittingRts(false);
    }
  };

  const handlePrintSlip = () => {
    if (!rtsResult) return;
    const w = window.open('', '_blank');
    const rows = rtsResult.usn_details.map((u, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${u.usn_code}</td>
        <td>${u.sort_no || '-'}</td>
        <td>${u.design_name || '-'}</td>
        <td>${u.shade || '-'}</td>
        <td>${u.size || '-'}</td>
        <td>${u.description || '-'}</td>
      </tr>`).join('');
    w.document.write(`<html><head><title>Return to Supplier - ${rtsResult.slip_id}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        h2 { text-align: center; margin-bottom: 5px; }
        .meta { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 14px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th, td { border: 1px solid #333; padding: 6px 8px; text-align: left; }
        th { background: #f0f0f0; }
        .footer { margin-top: 30px; display: flex; justify-content: space-between; font-size: 13px; }
        @media print { body { margin: 10px; } }
      </style></head><body>
      <h2>Return to Supplier Slip</h2>
      <p style="text-align:center;font-size:14px;color:#666">${rtsResult.slip_id}</p>
      <div class="meta">
        <div><strong>Vendor:</strong> ${rtsResult.vendor_name}</div>
        <div><strong>Date:</strong> ${new Date(rtsResult.created_at).toLocaleDateString()}</div>
      </div>
      <div class="meta">
        <div><strong>Staff:</strong> ${rtsResult.staff_name}</div>
        <div><strong>Total USNs:</strong> ${rtsResult.total_usns}</div>
      </div>
      <table>
        <thead><tr><th>#</th><th>USN Code</th><th>Sort No</th><th>Design Name</th><th>Shade</th><th>Size</th><th>Description</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">
        <div>Prepared By: _______________</div>
        <div>Received By: _______________</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
    w.document.close();
  };

  const filteredDamageTypes = damageTypes.filter(d =>
    d.damage_name?.toLowerCase().includes(damageSearch.toLowerCase()) ||
    d.damage_code?.toLowerCase().includes(damageSearch.toLowerCase())
  );

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-gray-50" data-testid="damage-checking-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => mode === 'return-to-supplier' ? setMode('scan') : navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-gray-900">
            {mode === 'return-to-supplier' ? 'Return to Supplier' : 'Damage Checking'}
          </h1>
          <p className="text-xs text-gray-500">
            {mode === 'return-to-supplier' ? 'Return damaged USNs to vendor' : 'Inspect folded pieces for damages'}
          </p>
        </div>
        {mode === 'scan' && staff && (
          <div className="flex items-center gap-2 bg-amber-50 px-3 py-1.5 rounded-lg" data-testid="staff-badge">
            <UserCheck className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">{staff.staff_name}</span>
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* ===== STAFF SCAN ===== */}
        {mode === 'staff' && (
          <Card className="border-amber-200">
            <CardHeader className="text-center pb-2">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <CardTitle className="text-base">Scan Staff ID</CardTitle>
              <p className="text-sm text-gray-500">Enter your Staff ID to begin damage checking</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleStaffSubmit} className="flex gap-2">
                <Input ref={staffInputRef} value={staffValue} onChange={e => setStaffValue(e.target.value)}
                  placeholder="Staff ID" autoFocus data-testid="staff-input" />
                <Button type="submit" disabled={validating || !staffValue.trim()} data-testid="staff-submit">
                  {validating ? '...' : 'Go'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ===== USN SCAN + DECISION ===== */}
        {mode === 'scan' && (
          <>
            <Card>
              <CardContent className="pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Scan USN Code</label>
                <form onSubmit={handleUsnLookup} className="flex gap-2">
                  <Input ref={usnInputRef} value={usnValue} onChange={e => setUsnValue(e.target.value)}
                    placeholder="Scan USN barcode..." autoFocus data-testid="usn-scan-input" />
                  <Button type="submit" disabled={scanning || !usnValue.trim()} data-testid="usn-lookup-btn">
                    {scanning ? '...' : <Search className="w-4 h-4" />}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* USN Details + Decision */}
            {currentUsn && (
              <Card className="border-amber-200 bg-amber-50/30" data-testid="usn-detail-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-5 h-5 text-amber-600" />
                    {currentUsn.usn_code}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Bundle</span><p className="font-semibold">{currentUsn.bundle_code}</p></div>
                    <div><span className="text-gray-500">Design</span><p className="font-semibold">{currentUsn.design_name || '-'}</p></div>
                    <div><span className="text-gray-500">Shade</span><p className="font-semibold">{currentUsn.shade || '-'}</p></div>
                    <div><span className="text-gray-500">SKU</span><p className="font-mono font-semibold">{currentUsn.sku_code || '-'}</p></div>
                    {currentUsn.sort_no && <div><span className="text-gray-500">Sort No</span><p className="font-semibold">{currentUsn.sort_no}</p></div>}
                    <div><span className="text-gray-500">Party Name</span><p className="font-semibold" data-testid="usn-party-name">{currentUsn.vendor_name || '-'}</p></div>
                  </div>

                  {currentUsn.pjo_id && <PjoOutcomePick joId={currentUsn.pjo_id} value={pjoOutcome} onChange={setPjoOutcome} />}

                  <div className="flex gap-3 pt-3 border-t">
                    <Button className="flex-1 bg-green-600 hover:bg-green-700" size="lg" onClick={handleCleared} data-testid="cleared-btn">
                      <CheckCircle className="w-4 h-4 mr-2" /> Damage Cleared
                    </Button>
                    <Button className="flex-1 bg-red-600 hover:bg-red-700" size="lg" onClick={handleNotCleared} data-testid="not-cleared-btn">
                      <XCircle className="w-4 h-4 mr-2" /> Not Cleared
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Processed list */}
            {processedList.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Processed ({processedList.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {processedList.map((item, idx) => (
                      <div key={item.usn_code} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm">
                        <span className="font-mono">{item.usn_code}</span>
                        <span className="flex items-center gap-1">
                          {item.pjo_outcome && (
                            <Badge className={item.pjo_outcome === 'REJECTED' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'} data-testid={`processed-outcome-${item.usn_code}`}>
                              {item.pjo_id} · {item.pjo_outcome === 'REJECTED' ? 'Rejected' : 'Repaired'}
                            </Badge>
                          )}
                          <Badge className={item.result === 'CLEARED' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                            {item.result === 'CLEARED' ? 'Cleared' : `DT → ${item.ne_barcode}`}
                          </Badge>
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Return to Supplier button */}
            <Button variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50" onClick={openReturnToSupplier} data-testid="return-supplier-btn">
              <Truck className="w-4 h-4 mr-2" /> Return to Supplier
            </Button>

            {/* Saved return documents — create boxes & print supplier slips */}
            <Button variant="ghost" className="w-full text-gray-600 hover:bg-gray-100" onClick={() => navigate('/sections/inhouse/return-to-supplier/documents')} data-testid="rts-documents-btn">
              <Package className="w-4 h-4 mr-2" /> Saved Return Documents (Create Box / Print)
            </Button>
          </>
        )}

        {/* ===== RETURN TO SUPPLIER ===== */}
        {mode === 'return-to-supplier' && !rtsResult && (
          <>
            {/* RTS Staff */}
            {!rtsStaff ? (
              <Card className="border-red-200">
                <CardHeader className="text-center pb-2">
                  <Truck className="w-10 h-10 text-red-500 mx-auto mb-2" />
                  <CardTitle className="text-base">Scan Staff ID</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRtsStaffSubmit} className="flex gap-2">
                    <Input ref={rtsStaffRef} value={rtsStaffValue} onChange={e => setRtsStaffValue(e.target.value)}
                      placeholder="Staff ID" autoFocus data-testid="rts-staff-input" />
                    <Button type="submit" disabled={!rtsStaffValue.trim()} data-testid="rts-staff-submit">Verify</Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                  <UserCheck className="w-4 h-4 text-red-600" />
                  <span className="font-medium">{rtsStaff.staff_name}</span>
                </div>

                {/* Vendor select */}
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Select Vendor</label>
                      <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                        <SelectTrigger data-testid="vendor-select">
                          <SelectValue placeholder="Select vendor..." />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors.map(v => (
                            <SelectItem key={v.vendor_name} value={v.vendor_name}>{v.vendor_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Scan USN Codes</label>
                      <form onSubmit={handleRtsUsnScan} className="flex gap-2">
                        <Input ref={rtsUsnRef} value={rtsUsnValue} onChange={e => setRtsUsnValue(e.target.value)}
                          placeholder="Scan USN..." data-testid="rts-usn-input" />
                        <Button type="submit" disabled={!rtsUsnValue.trim()} data-testid="rts-usn-submit">
                          <ScanBarcode className="w-4 h-4" />
                        </Button>
                      </form>
                      <p className="text-xs text-slate-500 mt-1">One scan packs the piece into the current box. Use &quot;Close Box &amp; Start New&quot; to split into multiple boxes — no re-scanning.</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Completed (closed) boxes */}
                {rtsBoxes.map((box, boxIdx) => (
                  <Card key={`box-${boxIdx}`} className="border-blue-200" data-testid={`rts-box-${boxIdx + 1}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="w-4 h-4 text-blue-600" /> Box {boxIdx + 1} ({box.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {box.map((u, idx) => (
                          <div key={u.usn_code} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm">
                            <div>
                              <span className="font-mono font-medium">{u.usn_code}</span>
                              <span className="text-gray-400 ml-2">{u.design_name}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeBoxUsn(boxIdx, idx)}
                              data-testid={`remove-box-usn-${boxIdx + 1}-${u.usn_code}`}>
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Current open box */}
                <Card className="border-amber-300" data-testid="rts-current-box">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Package className="w-4 h-4 text-amber-600" /> Box {rtsBoxes.length + 1} — Packing ({rtsCurrentBox.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {rtsCurrentBox.length > 0 ? (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {rtsCurrentBox.map((u, idx) => (
                          <div key={u.usn_code} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm">
                            <div>
                              <span className="font-mono font-medium">{u.usn_code}</span>
                              <span className="text-gray-400 ml-2">{u.design_name}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeCurrentUsn(idx)}
                              data-testid={`remove-current-usn-${u.usn_code}`}>
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 py-2 text-center">Scan USNs to fill this box…</p>
                    )}
                    <Button variant="outline" className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                      onClick={closeCurrentBox} disabled={rtsCurrentBox.length === 0} data-testid="close-box-btn">
                      <Package className="w-4 h-4 mr-2" /> Close Box &amp; Start New
                    </Button>
                  </CardContent>
                </Card>

                <Button className="w-full bg-red-600 hover:bg-red-700" size="lg"
                  onClick={handleRtsSubmit} disabled={submittingRts || !selectedVendor || (rtsBoxes.flat().length + rtsCurrentBox.length) === 0}
                  data-testid="rts-submit-btn">
                  {submittingRts ? 'Submitting...' : (() => {
                    const totalUsns = rtsBoxes.flat().length + rtsCurrentBox.length;
                    const totalBoxes = rtsBoxes.length + (rtsCurrentBox.length > 0 ? 1 : 0);
                    return `Submit & Pack — ${totalUsns} USN${totalUsns !== 1 ? 's' : ''} in ${totalBoxes} box${totalBoxes !== 1 ? 'es' : ''}`;
                  })()}
                </Button>
              </>
            )}
          </>
        )}

        {/* RTS Result */}
        {mode === 'return-to-supplier' && rtsResult && (
          <>
            <Card className="border-green-200 bg-green-50/30" data-testid="rts-result-card">
              <CardContent className="pt-6">
                <div className="text-center mb-4">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                  <h2 className="text-lg font-bold text-green-800">Return Slip Created &amp; Packed</h2>
                  <p className="text-sm font-mono text-gray-600">{rtsResult.slip_id}</p>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm mb-4">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Vendor</p>
                    <p className="font-bold text-xs">{rtsResult.vendor_name}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">USNs</p>
                    <p className="text-xl font-bold text-red-600">{rtsResult.total_usns}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Boxes</p>
                    <p className="text-xl font-bold text-blue-600">{boxes.length}</p>
                  </div>
                </div>
                <Button className="w-full" onClick={handlePrintSlip} data-testid="print-slip-btn">
                  <Printer className="w-4 h-4 mr-2" /> Print A4 Slip
                </Button>
              </CardContent>
            </Card>

            {/* Packed boxes (auto-created during the single scan) */}
            {boxes.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Boxes ({boxes.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {boxes.map(box => (
                      <div key={box.box_number} className="bg-gray-50 rounded-lg p-3" data-testid={`result-box-${box.box_number}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono font-bold text-sm">{box.box_number}</span>
                          <Badge>{box.total_qty} USNs</Badge>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(box.usn_codes || []).map(u => (
                            <span key={u} className="text-xs font-mono bg-white px-1.5 py-0.5 rounded border">{u}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Button variant="outline" className="w-full" onClick={openReturnToSupplier} data-testid="rts-new-return-btn">
              Start New Return
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setMode('scan')}>
              Back to Damage Checking
            </Button>
          </>
        )}
      </div>

      {/* Hidden camera input */}
      <input type="file" accept="image/*" capture="environment" ref={cameraInputRef}
        className="hidden" onChange={handleFileChange} />

      {/* ===== DAMAGE DIALOG ===== */}
      <Dialog open={showDamageDialog} onOpenChange={setShowDamageDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Damage Details — {currentUsn?.usn_code}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-2">
            {/* Damage category (source) */}
            <div>
              <label className="text-sm font-medium mb-1 block">Damage Category <span className="text-red-500">*</span></label>
              <Select value={damageCategory} onValueChange={setDamageCategory}>
                <SelectTrigger data-testid="damage-category-select">
                  <SelectValue placeholder="Select damage category" />
                </SelectTrigger>
                <SelectContent>
                  {DAMAGE_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat} data-testid={`damage-category-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Damage type selection */}
            <div>
              <label className="text-sm font-medium mb-1 block">Select Damage Types</label>
              <Input value={damageSearch} onChange={e => setDamageSearch(e.target.value)}
                placeholder="Search damages..." className="mb-2" data-testid="damage-search" />
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {filteredDamageTypes.map(d => (
                  <div key={d.damage_code}
                    className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer text-sm
                      ${selectedDamages.includes(d.damage_code) ? 'bg-red-50 border border-red-200' : 'hover:bg-gray-50'}`}
                    onClick={() => toggleDamage(d.damage_code)} data-testid={`damage-${d.damage_code}`}>
                    <span>{d.damage_name}</span>
                    {selectedDamages.includes(d.damage_code) && <CheckCircle className="w-4 h-4 text-red-600" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Photo upload per damage */}
            {selectedDamages.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-1 block">Photos (required per damage)</label>
                {selectedDamages.map(code => {
                  const dt = damageTypes.find(d => d.damage_code === code);
                  return (
                    <div key={code} className="border rounded-lg p-2 mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{dt?.damage_name}</span>
                        <Button variant="outline" size="sm" onClick={() => handleCameraCapture(code)} disabled={uploadingImage}>
                          <Camera className="w-3.5 h-3.5 mr-1" /> {uploadingImage && activeDamageCode === code ? 'Uploading...' : 'Photo'}
                        </Button>
                      </div>
                      {damageImages[code]?.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                          {damageImages[code].map((url, i) => (
                            <img key={i} src={url} alt="" className="w-16 h-16 rounded object-cover border" />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* No Exchange item selection */}
            <div>
              <label className="text-sm font-medium mb-1 block">Design Transfer to No Exchange</label>
              <Input value={noExchangeSearch} onChange={e => searchNoExchange(e.target.value)}
                placeholder="Search No Exchange items..." data-testid="ne-search" />
              {loadingNE && <p className="text-xs text-gray-400 mt-1">Loading...</p>}
              {noExchangeItems.length > 0 && (
                <div className="max-h-32 overflow-y-auto border rounded-lg p-2 mt-1 space-y-1">
                  {noExchangeItems.map(item => (
                    <div key={item.barcode}
                      className={`px-2 py-1.5 rounded cursor-pointer text-sm
                        ${selectedNoExchange?.barcode === item.barcode ? 'bg-orange-50 border border-orange-200' : 'hover:bg-gray-50'}`}
                      onClick={() => setSelectedNoExchange(item)} data-testid={`ne-${item.barcode}`}>
                      <span className="font-mono">{item.barcode}</span>
                      <span className="ml-2">{item.design_name}</span>
                      {item.shade && <span className="text-gray-400 ml-1">/ {item.shade}</span>}
                    </div>
                  ))}
                </div>
              )}
              {selectedNoExchange && (
                <div className="mt-2 p-2 bg-orange-50 rounded-lg text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-600" />
                  <span className="font-mono">{selectedNoExchange.barcode}</span> — {selectedNoExchange.design_name}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDamageDialog(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleSubmitDamage} data-testid="save-damage-btn">
              Confirm & Design Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DamageChecking;
